import { isWholeNumber } from "helpers/Helpers";

export interface IColorPickerRgb {
  r: number;
  g: number;
  b: number;
}

export interface IColorPickerRgbInput {
  r: string;
  g: string;
  b: string;
}

export interface IColorPickerHsv {
  h: number;
  s: number;
  v: number;
}

export function isValidRgbInput(rgbInput: IColorPickerRgbInput) {
  if (!isWholeNumber(rgbInput.r) || !isWholeNumber(rgbInput.g) || !isWholeNumber(rgbInput.b)) {
    return false;
  }

  const red = Number(rgbInput.r);
  const green = Number(rgbInput.g);
  const blue = Number(rgbInput.b);

  const colorInBounds = (color: number) => color >= 0 && color <= 255;

  return colorInBounds(red) && colorInBounds(green) && colorInBounds(blue);
}

export function rgbToRgbInput(rgb: IColorPickerRgb): IColorPickerRgbInput {
  return {
    r: String(rgb.r),
    g: String(rgb.g),
    b: String(rgb.b),
  };
}

export function rgbInputToRgb(rgb: IColorPickerRgbInput): IColorPickerRgb {
  return {
    r: Number(rgb.r),
    g: Number(rgb.g),
    b: Number(rgb.b),
  };
}

export function calculateRgbFromHexColor(hexColor: string): IColorPickerRgb {
  const hexToRgbMatch = hexToRgbRegex.exec(hexColor);

  if (hexToRgbMatch) {
    // eslint-disable-next-line no-unused-vars
    const [_, redHex, greenHex, blueHex] = hexToRgbMatch;

    return {
      r: parseInt(redHex, 16),
      g: parseInt(greenHex, 16),
      b: parseInt(blueHex, 16),
    };
  } else {
    return {
      r: 0,
      g: 0,
      b: 0,
    };
  }
}

export function calculateRgbFromHsv(hsv: IColorPickerHsv): IColorPickerRgb {
  const s = hsv.s / 100;
  const v = hsv.v / 100;
  const c = s * v;
  const h = (hsv.h / 60) % 6;
  const x = c * (1 - Math.abs((h % 2) - 1));

  let r1 = 0;
  let g1 = 0;
  let b1 = 0;

  if (h < 1) {
    [r1, g1, b1] = [c, x, 0];
  } else if (h < 2) {
    [r1, g1, b1] = [x, c, 0];
  } else if (h < 3) {
    [r1, g1, b1] = [0, c, x];
  } else if (h < 4) {
    [r1, g1, b1] = [0, x, c];
  } else if (h < 5) {
    [r1, g1, b1] = [x, 0, c];
  } else if (h < 6) {
    [r1, g1, b1] = [c, 0, x];
  }

  const m = v - c;

  const r = Math.round((r1 + m) * 255);
  const g = Math.round((g1 + m) * 255);
  const b = Math.round((b1 + m) * 255);

  return {
    r,
    g,
    b,
  };
}

const hexToRgbRegex = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/;

export function isValidHexColor(hexColor: string): boolean {
  return hexToRgbRegex.test(hexColor);
}

export function calculateHexColor(rgb: IColorPickerRgb): string {
  const rgbColorToHex = (color: number) => color.toString(16).toUpperCase().padStart(2, "0");

  const hexColor = "#" + rgbColorToHex(rgb.r) + rgbColorToHex(rgb.g) + rgbColorToHex(rgb.b);

  return hexColor;
}

export function calculateHsv(rgb: IColorPickerRgb): IColorPickerHsv {
  const hsv: IColorPickerHsv = {
    h: 0,
    s: 0,
    v: 0,
  };

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  const c = max - min;

  if (c === 0) {
    hsv.h = 0;
  } else if (max === r) {
    hsv.h = 60 * (((g - b) / c) % 6);
  } else if (max === g) {
    hsv.h = 60 * ((b - r) / c + 2);
  } else if (max === b) {
    hsv.h = 60 * ((r - g) / c + 4);
  }

  if (hsv.h < 0) {
    hsv.h += 360;
  }

  hsv.s = max === 0 ? 0 : (c / max) * 100;

  hsv.v = max * 100;

  return hsv;
}
